import { toIsoDate } from '@/core/models/practice.models';
import {
  ensureTrailingRows,
  formatAmount,
  formatGbp,
  normaliseAmounts,
  normaliseEditLine,
  recalculateTrialBalance,
  setLineClassification,
  toEditLines,
  type AccountTypeOption,
  type ChartAccountOption,
  type ChartAccountRow,
  type TrialBalanceDetail,
  type TrialBalanceLineInput,
  TrialBalanceEditLine,
  TrialBalanceEditModel,
} from '@/core/models/trial-balance.models';
import { trialBalanceService } from '@/core/services/trialBalance';

/** Route of the trial balance list (Angular LIST_ROUTE). */
export const TB_LIST_ROUTE = '/home/taxreturns/tasks-trialbalance';
/** Matrix segment name and base path of the editor (Angular EDIT_ROUTE). */
export const TB_EDIT_BASE = '/home/taxreturns';
export const TB_EDIT_SEGMENT = 'tasks-createtrialbalance';
/** APT366: the read-only statement view of a saved trial balance (a new page, so no Angular route; the id is the plain trial balance id). */
export const TB_VIEW_ROUTE = `${TB_EDIT_BASE}/tasks-viewtrialbalance`;
export const tbViewPath = (id: string) => `${TB_VIEW_ROUTE}/${encodeURIComponent(id)}`;

/** The server's figures replace the locally calculated ones (Angular `applyServerTotals`). */
export function applyServerTotals(model: TrialBalanceEditModel, detail: TrialBalanceDetail): void {
  model.totalDebit = detail.totalDebit;
  model.totalCredit = detail.totalCredit;
  model.difference = detail.difference;
  model.isBalanced = detail.isBalanced;
  model.profit = detail.profit;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2024-03-31` (or a dayjs / Date) as `31-Mar-2024`; empty when it cannot be read (Overview `displayDate`). */
export function displayDate(value: unknown): string {
  const iso = toIsoDate(value);
  const match = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  return match ? `${match[3]}-${MONTHS[Number(match[2]) - 1]}-${match[1]}` : '';
}

/**
 * #193: the client's chart as a saved trial balance carries it (`usp_TrialBalance_Get`: active accounts, the type's
 * name and statement area, by code), built from the Chart of Accounts list and its account types. A new trial balance
 * has not been saved yet, so without this its Account picker stayed empty until the first edit was autosaved.
 */
export function toChartAccountOptions(rows: ChartAccountRow[], types: AccountTypeOption[]): ChartAccountOption[] {
  const areaOf = new Map(types.map((t) => [t.id, t.statementArea]));
  return rows
    .filter((r) => r.isActive && areaOf.has(r.accountTypeId))
    .map((r) => ({
      code: r.code,
      name: r.accountName,
      accountType: r.category,
      statementArea: areaOf.get(r.accountTypeId)!,
    }))
    .sort((a, b) => a.code.localeCompare(b.code));
}

/** #193: loads the client's chart for a trial balance that has not been saved yet. */
export async function loadChartAccounts(clientId: string): Promise<ChartAccountOption[]> {
  const [rows, options] = await Promise.all([
    trialBalanceService.getAccounts(clientId, { search: null, status: 1, accountTypeId: null }),
    trialBalanceService.getAccountOptions(),
  ]);
  return toChartAccountOptions(rows, options.accountTypes ?? []);
}

/**
 * BUG-040: what a search that matched no account fills into Add New Account. Account codes are digits, so a leading
 * run of digits is the code and the rest (after an optional "-") is the name, as an account reads in the picker
 * ("5580 - Directors Loan"); anything else is taken as the name.
 */
export function newAccountPrefill(searchTerm: string): { accountCode?: string; accountName?: string } {
  const match = /^\s*(\d+)(?:\s*-\s*|\s+|$)(.*)$/.exec(searchTerm);
  if (match) {
    const name = match[2].trim();
    return name ? { accountCode: match[1], accountName: name } : { accountCode: match[1] };
  }
  const name = searchTerm.trim();
  return name ? { accountName: name } : {};
}

/** One row of the account picker on a trial balance line. */
export interface AccountPickOption {
  code: string;
  name: string;
  accountType: string;
  searchText: string;
}

/** Changes only when the chart or the set of codes on screen changes (Angular `accountOptionsKey`). */
export function accountOptionsSignature(model: TrialBalanceEditModel): string {
  return `${model.accounts?.length ?? 0}|${(model.lines ?? []).map((l) => (l.accountCode ?? '').trim()).join(',')}`;
}

/**
 * The client's chart, plus any code already on a line that the chart lacks, so imported history still displays
 * (Angular `buildAccountOptions`).
 */
export function buildAccountOptions(model: TrialBalanceEditModel): AccountPickOption[] {
  const options: AccountPickOption[] = (model.accounts ?? []).map((a) => ({
    code: a.code,
    name: a.name,
    accountType: a.accountType,
    searchText: `${a.code} ${a.name}`,
  }));

  const seen = new Set(options.map((o) => o.code.trim().toUpperCase()));
  for (const line of model.lines ?? []) {
    const code = (line.accountCode ?? '').trim();
    if (!code || seen.has(code.toUpperCase())) {
      continue;
    }
    seen.add(code.toUpperCase());
    options.push({
      code,
      name: line.particular?.trim() || 'Not in the chart of accounts',
      accountType: '',
      searchText: `${code} ${line.particular ?? ''}`,
    });
  }

  options.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  return options;
}

/** Whether a line is the last of the grid (Angular compared by reference). */
export function isLastLine(lines: TrialBalanceEditLine[], line: TrialBalanceEditLine): boolean {
  return lines.length > 0 && lines[lines.length - 1] === line;
}

/**
 * Holds the one shared, mutable trial balance model (Angular: `this.model`). The panels change it in place through
 * `edit()` and the editor re-renders them by bumping a revision counter, exactly as the Angular parent did.
 */
export class ModelHolder {
  current: TrialBalanceEditModel | null = null;

  set(model: TrialBalanceEditModel | null): void {
    this.current = model;
  }
}

/**
 * BUG-040: the client's chart, reloaded after an account was added from the trial balance. The chart is not part of
 * the trial balance, so this is not an edit: nothing is marked changed and nothing is saved.
 */
export function applyChartAccounts(model: TrialBalanceEditModel, accounts: ChartAccountOption[]): void {
  model.accounts = accounts;
}

/** How a panel changes the shared model: the editor applies `mutate`, recalculates, refreshes and marks it unsaved. */
export type EditModel = (mutate: (model: TrialBalanceEditModel) => void) => void;

/**
 * After a successful save: the record's identity, posting state and chart come from the server, and so do the totals
 * unless the user typed during the request (then the newer local figures are kept).
 */
export function applySavedDetail(
  model: TrialBalanceEditModel,
  detail: TrialBalanceDetail,
  changedDuringRequest: boolean,
  focusedAmountLine: number | null = null,
): void {
  model.id = detail.id;
  model.isPosted = detail.isPosted;
  model.postedAtUtc = detail.postedAtUtc;
  model.isUsedByReport = detail.isUsedByReport ?? model.isUsedByReport;
  model.accounts = detail.accounts ?? model.accounts;
  if (changedDuringRequest) {
    recalculateTrialBalance(model);
  } else {
    applySavedAmounts(model, detail, focusedAmountLine);
    applyServerTotals(model, detail);
  }
}

/**
 * BUG-039: once saved, each line shows the amounts the server stored - two decimals, one side - without reopening
 * the trial balance. The saved lines are the lines with an account, in order; a line is updated only when it is the
 * saved line's account, and never while its Debit or Credit box has focus (that one is shown as stored when it is
 * left), so a save never rewrites what is being typed.
 */
export function applySavedAmounts(
  model: TrialBalanceEditModel,
  detail: Pick<TrialBalanceDetail, 'lines'>,
  focusedAmountLine: number | null,
): void {
  const saved = detail.lines ?? [];
  const coded = model.lines.map((line, index) => ({ line, index })).filter(({ line }) => !!line.accountCode?.trim());
  if (coded.length !== saved.length) {
    return;
  }
  coded.forEach(({ line, index }, position) => {
    const stored = saved[position];
    if ((stored.accountCode ?? '').trim().toUpperCase() !== line.accountCode.trim().toUpperCase()) {
      return;
    }
    // FRS import (14-Oct): TB profit - the saved line's area as the accounts give it (the server classified the line as saved).
    setLineClassification(line, stored);
    if (index === focusedAmountLine) {
      return;
    }
    const amounts = normaliseAmounts(stored.debit, stored.credit);
    // BUG-039: a line whose amount is what was stored keeps the side it was typed on (a negative stays -500.00),
    // shown with two decimals.
    const shown = normaliseAmounts(line.debit, line.credit);
    if (shown.debit === amounts.debit && shown.credit === amounts.credit) {
      normaliseEditLine(line);
      return;
    }
    line.debit = formatAmount(amounts.debit);
    line.credit = formatAmount(amounts.credit);
  });
}

/** BUG-039: the grid line whose Debit or Credit box has focus (data-tb-amount-line), or null. */
export function focusedAmountLine(): number | null {
  const value = typeof document === 'undefined' ? null : document.activeElement?.getAttribute('data-tb-amount-line');
  return value === null || value === undefined || value === '' ? null : Number(value);
}

/**
 * BUG-036: replaces the lines with those read from a file or carried forward (Angular `applyLines`). Nothing is stored:
 * the caller marks the trial balance unsaved and its Save stores the lines once they balance.
 */
export function applyImportedLines(model: TrialBalanceEditModel, lines: TrialBalanceLineInput[]): void {
  model.lines = ensureTrailingRows(toEditLines(lines));
  recalculateTrialBalance(model);
}

/**
 * Why a trial balance that saved fine still was not posted to its accounting period: unlike Save, posting requires
 * balance (the old application's rule, kept where it actually belongs). Names the totals and the difference as the
 * grid shows them (BUG-039: in pence, one side per line).
 */
export function outOfBalanceMessage(model: Pick<TrialBalanceEditModel, 'totalDebit' | 'totalCredit'>): string {
  const difference = model.totalDebit - model.totalCredit;
  return (
    `The trial balance is out of balance (Total Dr. ${formatGbp(model.totalDebit)}, Total Cr. ` +
    `${formatGbp(model.totalCredit)}, ${difference > 0 ? 'Debit' : 'Credit'} ${formatGbp(Math.abs(difference))}). ` +
    'It can be posted to the accounting period only once it is balanced.'
  );
}

/** The server posted the trial balance to its accounting period. */
export function applyPosted(model: TrialBalanceEditModel, detail: TrialBalanceDetail): void {
  model.isPosted = detail.isPosted;
  model.postedAtUtc = detail.postedAtUtc;
}
